import React, { useState, useEffect } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  People as PeopleIcon,
  LocalOffer as OfferIcon,
  Store as StoreIcon,
  TrendingUp as TrendingUpIcon,
  AccountBalanceWallet as WalletIcon,
  SwapHoriz as ExchangeIcon,
  PersonAdd as PersonAddIcon,
  LocationOn as LocationIcon,
  Star as StarIcon,
} from '@mui/icons-material';

// Import components
import LineChartCard from '../components/dashboard/LineChartCard';
import PieChartCard from '../components/dashboard/PieChartCard';
import ActivityList from '../components/dashboard/ActivityList';
import PromotionFilter from '../components/dashboard/PromotionFilter';
import MetricCard from '../components/dashboard/MetricCard';
import TopClientsCard from '../components/dashboard/TopClientsCard';

// Import services
import { getActivePromotions, getPromotionStats, getGeneralStats, PromotionStats } from '../services/promotionService';

// El filtro de promociones ahora obtiene los datos directamente de la API

const neighborhoodColors = ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF'];

// Sample data for top clients
const emptyClient = { id: '', name: '', avatar: '', value: 0 };
const emptyClients = Array(10).fill(0).map((_, i) => ({
  ...emptyClient,
  id: `empty-${i}`,
  name: `Cliente ${i + 1}`
}));

const topParticipatingClients = [
  { id: '1', name: 'Tienda Central', avatar: '', value: 128 },
  { id: '2', name: 'Supermercado Uno', avatar: '', value: 115 },
  { id: '3', name: 'MiniMarket Express', avatar: '', value: 98 },
  { id: '4', name: 'Bodega La Esquina', avatar: '', value: 87 },
  { id: '5', name: 'Tienda del Barrio', avatar: '', value: 76 },
  { id: '6', name: 'Almacén Don Pepe', avatar: '', value: 65 },
  { id: '7', name: 'Super Ahorro', avatar: '', value: 59 },
  { id: '8', name: 'La Bodeguita', avatar: '', value: 53 },
  { id: '9', name: 'El Rinconcito', avatar: '', value: 47 },
  { id: '10', name: 'Tienda 24/7', avatar: '', value: 42 },
];

const DashboardPage: React.FC = () => {
  const theme = useTheme();
  const [selectedPromotion, setSelectedPromotion] = useState<string>('');
  const [promotionTitle, setPromotionTitle] = useState<string>('');
  const [isPromotionFilterDisabled, setIsPromotionFilterDisabled] = useState<boolean>(false);
  const [promotionStats, setPromotionStats] = useState<PromotionStats | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Load general stats on component mount
  useEffect(() => {
    const loadGeneralStats = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const stats = await getGeneralStats();
        setPromotionStats(stats);
      } catch (err) {
        console.error('Error fetching general stats:', err);
        setError('Error al cargar las estadísticas generales');
      } finally {
        setIsLoading(false);
      }
    };

    loadGeneralStats();
  }, []);

  // Handle promotion selection
  const handleSelectPromotion = async (promotionId: string, promotionTitle: string = '') => {
    setSelectedPromotion(promotionId);
    setPromotionTitle(promotionTitle);
    
    setIsLoading(true);
    setError(null);
    
    try {
      let stats;
      if (!promotionId) {
        // Si no hay promoción seleccionada, obtener estadísticas generales
        stats = await getGeneralStats();
      } else {
        // Si hay promoción seleccionada, obtener estadísticas específicas
        stats = await getPromotionStats(promotionId);
      }
      setPromotionStats(stats);
    } catch (err) {
      console.error('Error fetching stats:', err);
      setError('Error al cargar las estadísticas');
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate metrics based on selected promotion
  const totalRedemptions = promotionStats?.total_trxs || 0;
  const totalConsumers = promotionStats?.total_consumers || 0;
  const participatingClients = promotionStats?.total_clients || 0;
  const averagePerClient = promotionStats?.average_trx_per_client != null 
    ? promotionStats.average_trx_per_client.toFixed(1) 
    : '0.0';
  
  // Chart data
  const redemptionsData = promotionStats?.trxs_per_day?.map(item => ({
    name: item.created_at,
    value: parseInt(item.count, 10)
  })) || [];

  const newUsersData = promotionStats?.new_consumers?.map(item => ({
    name: item.date,
    value: item.qty
  })) || [];

  const neighborhoodData = (promotionStats?.trxs_per_zip || [])
    .filter(item => parseInt(item.total, 10) > 0)
    .map(item => ({
      name: item.locality,
      value: parseInt(item.total, 10)
    }));

  const topRedemptionClients = promotionStats?.top_10_clients?.map(client => ({
    id: client.client_id,
    name: client.client_name,
    avatar: '',
    value: parseInt(client.qty, 10)
  })) || [];

  const topRedemptionConsumers = promotionStats?.top_10_consumers?.map(consumer => ({
    id: consumer.consumer_id,
    name: consumer.consumer_name,
    avatar: '',
    value: parseInt(consumer.qty, 10)
  })) || [];

  // Define trend type for type safety
  type TrendType = 'up' | 'down' | 'neutral';

  // Calculate trends (example data - replace with real data)
  const trends: Record<string, TrendType> = {
    redemptions: 'up',
    consumers: 'up',
    clients: 'down',
    average: 'up'
  } as const;

  const trendValues: Record<string, string> = {
    redemptions: '12%',
    consumers: '5%',
    clients: '3%',
    average: '8%'
  } as const;



  return (
    <Box
      component="main"
      sx={{
        p: { xs: 2, md: 3 },
        backgroundColor: theme.palette.background.default,
        minHeight: '100vh',
        maxWidth: '100%',
        overflowX: 'hidden',
        '&:focus-visible': {
          outline: 'none',
        },
      }}
    >
      <Box
        component="header"
        sx={{
          mb: 4,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2,
          '&:focus-visible': {
            outline: 'none',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
          <Typography
            component="h1"
            variant="h4"
            fontWeight="bold"
            sx={{
              color: '#C8102E',
              fontSize: { xs: '24px', sm: '28px' },
              lineHeight: 1.2,
              textShadow: '0 2px 4px rgba(0,0,0,0.05)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateX(4px)',
              },
            }}
          >
            Métricas
          </Typography>
          {selectedPromotion && (
            <>
              <Typography
                component="span"
                variant="h4"
                sx={{
                  color: '#C8102E',
                  fontSize: { xs: '24px', sm: '28px' },
                  lineHeight: 1.2,
                  fontWeight: 'bold',
                  textShadow: '0 2px 4px rgba(0,0,0,0.05)',
                }}
              >
                {' '}de{' '}
              </Typography>
              <Typography
                component="span"
                variant="h4"
                sx={{
                  color: '#C8102E',
                  fontSize: { xs: '24px', sm: '28px' },
                  lineHeight: 1.2,
                  fontWeight: 'bold',
                  textShadow: '0 2px 4px rgba(0,0,0,0.05)',
                  textTransform: 'uppercase',
                }}
              >
                {promotionTitle || (selectedPromotion ? 'Cargando...' : 'Todas las promociones')}
              </Typography>
            </>
          )}
        </Box>
        
        {/* Promotion Filter */}
        <Box
          sx={{
            width: { xs: '100%', sm: 'auto' },
            minWidth: { xs: '100%', sm: '300px' },
            maxWidth: '400px',
            '&:focus-visible': {
              outline: 'none',
            },
          }}
        >
          <PromotionFilter 
            selectedPromotion={selectedPromotion}
            onSelectPromotion={handleSelectPromotion}
            disabled={isPromotionFilterDisabled}
          />
        </Box>
      </Box>

      {/* Metrics Cards */}
      <Box
        component="section"
        aria-label="Métricas principales"
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            lg: 'repeat(4, 1fr)'
          },
          gap: { xs: 2, sm: 3 },
          mb: { xs: 3, md: 4 },
          '& > *': {
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: theme.shadows[4],
            },
            '&:focus-visible': {
              outline: 'none',
              boxShadow: `0 0 0 3px ${theme.palette.primary.light}`,
              transform: 'translateY(-2px)',
            },
          },
        }}
      >
        <Box>
          <MetricCard
            title="TOTAL PROMOCIONES UTILIZADAS"
            value={totalRedemptions.toLocaleString()}
            icon={<ExchangeIcon />}
            color="#C8102E"
          />
        </Box>
        <Box>
          <MetricCard
            title="CONSUMIDORES PARTICIPANTES"
            value={totalConsumers.toLocaleString()}
            icon={<PeopleIcon />}
            color="#2E7D32"
          />
        </Box>
        <Box>
          <MetricCard
            title="Clientes Participantes"
            value={participatingClients.toLocaleString()}
            icon={<StoreIcon />}
            color="#1565C0"
          />
        </Box>
        <Box>
          <Box sx={{ position: 'relative' }}>
            <MetricCard
              title="Promedio por Cliente"
              value={
                <Box component="span" sx={{ display: 'flex', alignItems: 'baseline' }}>
                  <span>{averagePerClient}</span>
                  <Typography component="span" sx={{ fontSize: '0.5em', ml: 0.5, color: 'black', whiteSpace: 'nowrap' }}>
                    promociones
                  </Typography>
                </Box>
              }
              icon={<TrendingUpIcon />}
              color="#7B1FA2"
            />
          </Box>
        </Box>
      </Box>

      {/* First Row Charts */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3 }}>
          <Box>
            <LineChartCard
              title="Promos utilizadas por día"
              data={redemptionsData}
              color="#C8102E"
              height={350}
              xAxisLabel="Fecha"
              yAxisLabel="Cantidad"
            />
          </Box>
          <Box>
            <PieChartCard
              title="Distribución por Barrio"
              data={neighborhoodData}
              height={350}
              colors={['#C8102E', '#2E7D32', '#1565C0', '#7B1FA2', '#FF9800']}
            />
          </Box>
        </Box>
      </Box>

      {/* Expanded Nuevos Consumidores Chart */}
      <Box sx={{ mb: 4 }}>
        <LineChartCard
          title="Nuevos Consumidores"
          data={newUsersData}
          color="#f44336"
          height={350}
          xAxisLabel="Fecha"
          yAxisLabel="Consumidores"
          areaGradient={true}
        />
      </Box>

      {/* Top Clients */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          <TopClientsCard 
            title="TOP 10 CONSUMIDORES"
            clients={topRedemptionConsumers}
            valueSuffix="canjes"
            maxItems={10}
            color="#2E7D32"
            emptyMessage={!selectedPromotion ? "Selecciona una promoción para ver los consumidores" : "No hay datos de consumidores disponibles"}
          />
          <TopClientsCard 
            title="TOP 10 CLIENTES"
            clients={topRedemptionClients}
            valueSuffix="promociones"
            maxItems={10}
            color="#1565C0"
            emptyMessage={!selectedPromotion ? "Selecciona una promoción para ver los clientes" : "No hay datos de clientes disponibles"}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardPage;
